// src/utils/timeUtils.js

/**
 * 시간 문자열을 포맷팅합니다
 * @param {string} input - 포맷팅할 시간 문자열 (예: "01:23:45" 또는 "1:2:3")
 * @returns {string} - 포맷팅된 시간 문자열 (예: "01:23:45.000")
 */
export const formatTime = (input) => {
    const parts = input.split(/[:.]/);
    if (parts.length >= 3) {
      const [hh, mm, ss, ms] = parts;
      return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:${ss.padStart(2, '0')}${ms ? '.' + ms.padEnd(3, '0').slice(0, 3) : ''}`;
    }
    return input;
  };
  
  /**
   * 시간 문자열의 유효성을 검사합니다
   * @param {string} time - 검사할 시간 문자열 (예: "01:23:45.678")
   * @returns {boolean} - 유효한 시간인지 여부
   */
  export const validateTime = (time) => {
    const regex = /^([0-9]{2}):([0-5][0-9]):([0-5][0-9])(?:\.([0-9]{1,3}))?$/;
    const match = time.match(regex);
    
    if (!match) return false;
    
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);
    
    return hours <= 23 && minutes <= 59 && seconds <= 59;
  };
  
  /**
   * 포맷팅된 시간 문자열을 밀리초로 변환합니다
   * @param {string} timeString - 변환할 시간 문자열 (예: "01:23:45.678")
   * @returns {number} - 밀리초 단위의 시간
   */
  export const parseFormattedTime = (timeString) => {
    const parts = timeString.split(/[:.]/);
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parseInt(parts[2], 10);
    const milliseconds = parts.length > 3 ? parseInt(parts[3].padEnd(3, '0').slice(0, 3), 10) : 0;
    
    return ((hours * 3600) + (minutes * 60) + seconds) * 1000 + milliseconds;
  };
  
  /**
   * API에 자막 데이터를 저장합니다
   * @param {Array} subtitles - 저장할 자막 배열
   * @param {Object} params - API 호출에 필요한 파라미터
   * @returns {Promise} - API 호출 결과
   */
  export const saveSubtitlesToServer = (subtitles, { api, userToken, uuid, orgCode, lang }) => {
    const formatted = subtitles.map(list => ({
      identifier: String(list.id),
      start: list.start,
      end: list.end,
      text: list.content,
      styles: ""
    }));
  
    return api({
      method: 'post',
      url: '/subtitle/aUpdate',
      data: {
        tk: userToken,
        uuid,
        ogc: orgCode,
        lang,
        subtitle: formatted
      }
    });
  };
  
    /**
   * API에 자막 데이터를 저장합니다
   * @param {Array} chapters - 저장할 자막 배열
   * @param {Object} params - API 호출에 필요한 파라미터
   * @returns {Promise} - API 호출 결과
   */
    export const saveChaptersToServer = (chapters, { api, userToken, uuid, orgCode, lang }) => {
      const formatted = chapters.map(list => ({
        index: String(list.id),
        type: "seeker",
        start: list.start,
        label: list.label,
        thumbnail: "",
        desc: list.desc
      }));
    
      return api({
        method: 'post',
        url: '/chapter/aUpdate',
        data: {
          tk: userToken,
          uuid,
          ogc: orgCode,
          lang,
          chapter: formatted
        }
      });
    };

  /**
   * 시간 변경 입력을 처리합니다
   * @param {Event} e - 입력 이벤트
   * @param {Function} setColors - 색상 상태 설정 함수
   * @param {Function} setChangingItem - 변경 중인 아이템 상태 설정 함수
   * @param {Object} item - 현재 아이템
   * @param {string} timeField - 변경할 시간 필드 ('start' 또는 'end')
   * @returns {void}
   */
export const handleTimeInputChange = (
  e,
  { setColors, setChangingItem, timeField }
) => {
  const newValue = e.currentTarget.textContent;
  const formattedTime = formatTime(newValue);

  if (validateTime(formattedTime)) {
    const newTime = parseFormattedTime(formattedTime); // ms 단위
    const seconds = newTime / 1000;

    setColors(prev =>
      typeof prev === 'object' ? { ...prev, [timeField]: 'green' } : 'green'
    );

    setChangingItem(prev => ({
      ...prev,
      [timeField]: seconds
    }));

    return seconds; // ✅ 초 단위로 반환
  } else {
    setColors(prev =>
      typeof prev === 'object' ? { ...prev, [timeField]: 'red' } : 'red'
    );
    return null;
  }
};